import { useState, useCallback, useEffect } from 'react';
import { VStack, HStack, Text, Box } from '@chakra-ui/react';
import { useNavigate, SetURLSearchParams } from 'react-router-dom';
import { useMyClassesSearch, TeacherClassData } from 'hooks/api/backend/managament/getClasses';
import { Table } from 'components/common/Table/Table';
import { Pagination } from 'components/common/Table/Pagination';
import { createColumns } from 'utils/tableHelper';
import { SearchIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { Filters } from 'components/common/Table/Filters';
import { InputOption } from 'components/common/Table/FilterOptions/InputOption';
import { ProtectedRoutes } from 'types/Routes';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { TextCell, DefaultCell } from 'components/common/Table/Cells';
import { TextCellProps, CellProps } from 'components/common/Table/types/Cell';
import { NameList } from 'components/common/NameList/NameList';
import { ClassesTabFilter } from 'types/Worksheets';
import { useIsB2BTeacherOrAdmin } from 'hooks/utils/useIsB2BTeacherOrAdmin';
import { ClassesAccessDeniedMessage } from 'components/worksheet/dashboard/ClassesAccessDeniedMessage';
import { NoSearchResult } from 'components/common/NoSearchResult';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { NoTableContent } from 'components/common/NoTableContent';
import storage from 'utils/storage';

type ClassTableData = {
  id: string;
  name: string;
  studentsCount: string;
  worksheetsCount: string;
  teachersCount: string;
  matchType: {
    students: { id: string; name: string }[];
    className: string;
  };
  actionItem: null;
  b2bSchoolId: string;
};

const NameCell = ({ label }: TextCellProps): JSX.Element => (
  <TextCell label={label} textStyle="body.s.medium" textColor="text.primary" />
);

const StudentsCell = ({ label }: TextCellProps): JSX.Element => (
  <TextCell label={label} textStyle="body.s.regular" textColor="text.secondary" />
);

const MatchTypeCell = (props: CellProps): JSX.Element => {
  const { students, className } = props as unknown as {
    students: { id: string; name: string }[];
    className: string;
  };

  const studentNames = students.map((s) => s.name);
  const hasStudents = studentNames.length > 0;

  return (
    <VStack align="flex-start" gap="5px" py="10px" pr="10px">
      {hasStudents && (
        <Box bg="#f0edfe" px="10px" py="5px" borderRadius="l">
          <Text textStyle="body.xs.regular" color="#4d3c9e" as="span">
            Auszubildende:{' '}
            <NameList
              textStyle="body.xs.regular"
              textColor="#4d3c9e"
              names={studentNames}
              maxVisible={1}
            />
          </Text>
        </Box>
      )}
      {className && (
        <DefaultCell
          label={className}
          bgColor="#ffefda"
          px={5}
          py={10}
          borderRadius="l"
          textStyle="body.xs.regular"
          labelColor="#8d5610"
        />
      )}
    </VStack>
  );
};

interface ClassTabProps {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

export const ClassesTab = ({ searchParams, setSearchParams }: ClassTabProps): JSX.Element => {
  const selectedSchool = storage.getTenant();
  const navigate = useNavigate();
  const {
    isB2BUser,
    isB2BTeacherOrAdmin,
    isLoading: isB2BTeacherOrAdminLoading,
  } = useIsB2BTeacherOrAdmin();

  // Initialize from URL params
  const filterFromUrl = searchParams.get('f') as ClassesTabFilter;
  const pageFromUrl = searchParams.get('p');

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('s') || '');
  const [currentPage, setCurrentPage] = useState<number>(
    pageFromUrl ? parseInt(pageFromUrl, 10) : 1
  );
  const [activeFilter, setActiveFilter] = useState<ClassesTabFilter>(
    Object.values(ClassesTabFilter).includes(filterFromUrl)
      ? filterFromUrl
      : ClassesTabFilter.CLASSES
  );

  // Sync filters
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    if (searchQuery) {
      newParams.set('s', searchQuery);
    } else {
      newParams.delete('s');
    }

    if (activeFilter !== ClassesTabFilter.ALL) {
      newParams.set('f', activeFilter);
    } else {
      newParams.delete('f');
    }

    if (currentPage > 1) {
      newParams.set('p', currentPage.toString());
    } else {
      newParams.delete('p');
    }

    setSearchParams(newParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeFilter, currentPage]);

  const {
    data: classesData,
    isLoading: isSearchLoading,
    isError,
    error,
    refetch,
  } = useMyClassesSearch({
    page: currentPage,
    filter: activeFilter,
    searchQuery,
  });

  const handleSearchChange = useCallback((e: string) => {
    setSearchQuery(e);
    setCurrentPage(1);
  }, []);

  const handleManageClass = (classId: string): void => {
    navigate(`${ProtectedRoutes.WORKSHEET_CLASS_DETAIL}/${classId}`);
  };

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
  };

  const hasSearchQuery = searchQuery.length >= 1;
  const showMatchType = hasSearchQuery;

  const classColumns = createColumns<ClassTableData>()({
    name: {
      header: 'Name',
      element: NameCell,
      sortDisabled: true,
    },
    worksheetsCount: {
      header: 'Arbeitsblätter',
      element: StudentsCell,
      getCellProps: (row: ClassTableData) => ({
        label: row.worksheetsCount,
      }),
      sortDisabled: true,
    },
    studentsCount: {
      header: 'Auszubildende',
      element: StudentsCell,
      getCellProps: (row: ClassTableData) => ({
        label: row.studentsCount,
      }),
      sortDisabled: true,
    },

    ...(showMatchType && {
      matchType: {
        header: 'Match-Typ',
        element: MatchTypeCell,
        getCellProps: (row: ClassTableData) => ({
          students: row.matchType.students,
          className: row.matchType.className,
        }),
        sortDisabled: true,
      },
    }),
  });

  const handleClearSearch = (): void => {
    setSearchQuery('');
  };

  const handleFilterChange = (filter: ClassesTabFilter): void => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const mappedClasses: ClassTableData[] = (classesData?.data || []).map(
    (classItem: TeacherClassData) => {
      const studentCount = classItem.totalCount?.students ?? 0;
      const worksheetsCount = classItem.totalCount?.worksheets ?? 0;
      const studentsForMatch = classItem.students.map((s) => ({
        id: s.id,
        name: `${s.firstName} ${s.lastName}`,
      }));
      const hasClassNameMatch = !!(
        classItem.matchCount?.className && classItem.matchCount.className > 0
      );

      return {
        id: classItem.id,
        name: classItem.name,
        studentsCount: studentCount.toString(),
        worksheetsCount: worksheetsCount.toString(),
        teachersCount: '',
        matchType: {
          students: studentsForMatch,
          className: hasClassNameMatch ? classItem.name : '',
        },
        actionItem: null,
        b2bSchoolId: classItem.b2bSchoolId,
      };
    }
  );

  if (isB2BTeacherOrAdminLoading) {
    return <PageSpinner />;
  }

  if (!isB2BTeacherOrAdmin) {
    return <ClassesAccessDeniedMessage isB2BUser={isB2BUser} />;
  }

  const hasClasses = !!(mappedClasses && mappedClasses.length > 0);
  const showNoClassesState = !isSearchLoading && !hasClasses && !hasSearchQuery;
  const showNoResultsState = !isSearchLoading && !hasClasses && hasSearchQuery;

  const filterLabels: Partial<Record<ClassesTabFilter, string>> = {
    [ClassesTabFilter.CLASSES]: 'Meine Kurse',
    [ClassesTabFilter.ORGANIZATION_CLASSES]: 'Kurse meiner Organisation',
  };

  const getListContent = (): JSX.Element => {
    if (showNoClassesState) {
      return (
        <NoTableContent
          title="Du bist derzeit keinem Kurs zugewiesen."
          description="Bitte kontaktiere den Administrator deiner Organisation, um als Lehrperson für einen Kurs hinzugefügt zu werden."
          onClick={() => navigate(`${ProtectedRoutes.WORKSHEET_DASHBOARD}?t=worksheets`)}
          buttonText="Zu meinen Arbeitsblättern"
        />
      );
    }

    if (showNoResultsState) {
      return <NoSearchResult query={searchQuery} />;
    }

    return (
      <>
        <Table
          columns={classColumns}
          data={mappedClasses}
          isDataLoading={isSearchLoading}
          onRowClick={handleManageClass}
          tableRowProps={{
            cursor: 'pointer',
            _hover: {
              bg: 'background.canvas',
            },
          }}
        />
        {classesData?.totalPages && classesData.totalPages > 1 && (
          <Pagination
            totalPages={classesData.totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </>
    );
  };

  const showFilters = !showNoClassesState;

  if (isError) {
    return <QueryErrorState error={error} onRetry={() => refetch()} />;
  }

  return (
    <VStack gap="0" align="stretch">
      <HStack justifyContent="space-between" alignItems="center">
        <Text textStyle="h3.medium" color="text.primary">
          Kurse
        </Text>
      </HStack>

      <VStack
        align="left"
        bg="background.primary"
        w="full"
        gap="l"
        borderRadius={{ md: '2xl' }}
        mt="l"
      >
        {showFilters && (
          <>
            <Filters
              filters={[
                [
                  {
                    id: 'searchQuery',
                    element: InputOption,
                    placeholder: 'Kurs oder Name suchen',
                    value: searchQuery,
                    options: [],
                    disabled: !selectedSchool,
                    onChange: handleSearchChange,
                    icon: <NovaIcon icon={SearchIcon} size="m" aria-hidden />,
                    onClear: handleClearSearch,
                  },
                ],
              ]}
            />
            <HStack gap="15px" px="0">
              {Object.values(ClassesTabFilter)
                .filter((filter) => filterLabels[filter])
                .map((filter) => (
                  <Box
                    key={filter}
                    as="button"
                    bg={activeFilter === filter ? 'tag.background.green' : 'transparent'}
                    border="default"
                    borderColor={activeFilter === filter ? 'border.accent.active' : 'border.accent'}
                    px="10px"
                    py="5px"
                    borderRadius="5px"
                    cursor="pointer"
                    onClick={() => handleFilterChange(filter)}
                    _hover={{ opacity: '80' }}
                  >
                    <Text
                      textStyle="body.s.regular"
                      color={activeFilter === filter ? 'text.primary' : 'text.secondary'}
                    >
                      {`${filterLabels[filter]} (${classesData?.filterCounts?.[filter] ?? 0})`}
                    </Text>
                  </Box>
                ))}
            </HStack>
          </>
        )}
        {getListContent()}
      </VStack>
    </VStack>
  );
};
